import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import FlairEmblem from '../shop/FlairEmblem';
import './AdminShop.css';

/**
 * AdminShop — butiksadministration: lista skins ur drop-poolen till salu,
 * prissätta, avlista och överblicka köp.
 *
 * Alla anrop går via stats-backendens ``/api/shop/admin``-proxy som
 * vidarebefordrar JWT:n — Django-API:t gatar på webbkommandot
 * ``shop.manage`` (guild owner passerar alltid). Varor är unika skins:
 * ett köp avlistar automatiskt, så listan här visar även sålda/inaktiva
 * varor för historik.
 *
 * @param {object} props - Component props.
 * @param {object} props.perms - Admin permission helper from useAdminPermissions.
 * @returns {JSX.Element} Butiksadmin-panelen.
 */
export default function AdminShop({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [availableSkins, setAvailableSkins] = useState([]);
  // Emblembilderna på disk. Ett emblem är sitt filnamn, och ett felstavat
  // filnamn ger en vara med brutet bildkryss — därför en lista att välja ur
  // och inte ett fritextfält.
  const [availableEmblems, setAvailableEmblems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [raffles, setRaffles] = useState([]);
  const [raffleSkinId, setRaffleSkinId] = useState('');
  const [rafflePrice, setRafflePrice] = useState('25');
  const [raffleDrawsAt, setRaffleDrawsAt] = useState('');
  const [raffleMax, setRaffleMax] = useState('');
  const [raffleBusy, setRaffleBusy] = useState(false);
  const [newType, setNewType] = useState('skin');
  const [newSkinId, setNewSkinId] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newStock, setNewStock] = useState('');
  const [newFlairKind, setNewFlairKind] = useState('title');
  const [newFlairValue, setNewFlairValue] = useState('');
  // Emblemgenerering: skapa en ny bild ur ett motivbrief, utan skript i containern.
  const [emblemName, setEmblemName] = useState('');
  const [emblemMotif, setEmblemMotif] = useState('');
  const [emblemBusy, setEmblemBusy] = useState(false);
  const [emblemResult, setEmblemResult] = useState(null);
  const [emblemError, setEmblemError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editPrices, setEditPrices] = useState({});
  const [deliveringId, setDeliveringId] = useState(null);
  // Communitypotten (A4): saldo + allokering till en turnerings prispott.
  const [housePot, setHousePot] = useState(null);
  const [potTournaments, setPotTournaments] = useState([]);
  const [potTournamentId, setPotTournamentId] = useState('');
  const [potAmount, setPotAmount] = useState('');
  const [potBusy, setPotBusy] = useState(false);
  const [potMsg, setPotMsg] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = getAuthHeaders();
      const [itemsRes, skinsRes, purchasesRes, rafflesRes, emblemsRes] = await Promise.all([
        fetch(`${API_URL}/api/shop/admin/items`, { headers }),
        fetch(`${API_URL}/api/shop/admin/available-skins`, { headers }),
        fetch(`${API_URL}/api/shop/admin/purchases`, { headers }),
        fetch(`${API_URL}/api/shop/admin/raffles`, { headers }),
        fetch(`${API_URL}/api/shop/admin/available-emblems`, { headers }),
      ]);
      if (!itemsRes.ok) throw new Error('load failed');
      setItems((await itemsRes.json()).items || []);
      if (skinsRes.ok) setAvailableSkins((await skinsRes.json()).skins || []);
      if (emblemsRes.ok) setAvailableEmblems((await emblemsRes.json()).emblems || []);
      if (purchasesRes.ok) setPurchases((await purchasesRes.json()).purchases || []);
      if (rafflesRes.ok) setRaffles((await rafflesRes.json()).raffles || []);
      // Communitypotten + aktiva turneringar (allokeringsmål) — best-effort.
      const [potRes, tournRes] = await Promise.all([
        fetch(`${API_URL}/api/predictions/house-pot`, { headers }),
        fetch(`${API_URL}/api/tournaments?status=scheduled,registration_open,registration_closed,in_progress`, { headers }),
      ]);
      if (potRes.ok) setHousePot(await potRes.json());
      if (tournRes.ok) setPotTournaments(await tournRes.json() || []);
    } catch {
      setError(t('adminShop.loadError'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const canCreate = Boolean(newPrice) && (
    (newType === 'skin' && newSkinId)
    || (newType === 'custom' && newTitle.trim())
    || (newType === 'flair' && newTitle.trim() && newFlairValue.trim())
  );

  /**
   * Lista en ny vara (skin/custom/flair) till angivet poängpris.
   */
  const createItem = async () => {
    if (!canCreate) return;
    setSaving(true);
    setError('');
    const payload = { item_type: newType, price_points: Number(newPrice) };
    if (newType === 'skin') {
      payload.prize_id = newSkinId;
    } else {
      payload.title = newTitle.trim();
      payload.description = newDescription.trim();
      if (newStock) payload.stock = Number(newStock);
      if (newType === 'flair') {
        payload.flair_kind = newFlairKind;
        payload.flair_value = newFlairValue.trim();
      }
    }
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/items`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error || t('adminShop.saveError'));
      } else {
        setNewSkinId('');
        setNewPrice('');
        setNewTitle('');
        setNewDescription('');
        setNewStock('');
        setNewFlairValue('');
        loadAll();
      }
    } catch {
      setError(t('adminShop.saveError'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Generera ett nytt emblem ur ett motivbrief.
   *
   * v2-stilen läggs på i backend, så admin skriver bara motivet ("a wolf skull
   * seen from the front"). Vid lyckad generering hämtas emblemlistan om så det
   * nya motivet direkt går att lista som vara i dropdownen ovan.
   */
  const generateEmblem = async () => {
    if (!emblemName.trim() || !emblemMotif.trim()) return;
    setEmblemBusy(true);
    setEmblemError('');
    setEmblemResult(null);
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/emblems/generate`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: emblemName.trim(), motif: emblemMotif.trim() }),
      });
      const body = await res.json();
      if (!res.ok) {
        setEmblemError(body.error || t('adminShop.emblemGenFailed'));
      } else {
        setEmblemResult(body);
        setEmblemName('');
        setEmblemMotif('');
        // Nya bilden ska dyka upp i listnings-dropdownen direkt.
        const er = await fetch(`${API_URL}/api/shop/admin/available-emblems`, {
          headers: getAuthHeaders(),
        });
        if (er.ok) setAvailableEmblems((await er.json()).emblems || []);
      }
    } catch {
      setEmblemError(t('adminShop.emblemGenFailed'));
    } finally {
      setEmblemBusy(false);
    }
  };

  /**
   * Markera ett custom-köp som manuellt levererat.
   *
   * @param {object} purchase Köpet som levererats utanför plattformen.
   */
  const deliver = async (purchase) => {
    setDeliveringId(purchase.id);
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/purchases/${purchase.id}/deliver`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) loadAll();
      else setError((await res.json()).error || t('adminShop.saveError'));
    } catch {
      setError(t('adminShop.saveError'));
    } finally {
      setDeliveringId(null);
    }
  };

  /**
   * Spara nytt pris för en listad vara.
   *
   * @param {object} item Varan vars pris ändras.
   */
  const savePrice = async (item) => {
    const price = Number(editPrices[item.id]);
    if (!price || price <= 0) return;
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/items/${item.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ price_points: price }),
      });
      if (res.ok) {
        setEditPrices((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
        loadAll();
      }
    } catch {
      setError(t('adminShop.saveError'));
    }
  };

  /**
   * Slå på/av om varan får säljas vidare spelare emellan på marknaden.
   *
   * Handelsbarheten är ett eget val per vara och inte längre en bieffekt av
   * lagersaldot. Backend synkar valet till katalogposten, så ändringen gäller
   * även varor som redan finns i någons inventory.
   *
   * @param {object} item Varan vars handelsbarhet ändras.
   */
  const toggleTradeable = async (item) => {
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/items/${item.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ marketplace_tradeable: !item.marketplace_tradeable }),
      });
      if (res.ok) loadAll();
      else setError((await res.json()).error || t('adminShop.saveError'));
    } catch {
      setError(t('adminShop.saveError'));
    }
  };

  /**
   * Skapa ett lotteri av valt skin med pris, dragningstid och ev. maxgräns.
   */
  const createRaffle = async () => {
    if (!raffleSkinId || !rafflePrice || !raffleDrawsAt) return;
    setRaffleBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/raffles`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prize_id: raffleSkinId,
          ticket_price: Number(rafflePrice),
          draws_at: new Date(raffleDrawsAt).toISOString(),
          max_tickets_per_player: raffleMax ? Number(raffleMax) : null,
        }),
      });
      const body = await res.json();
      if (!res.ok) setError(body.error || t('adminShop.saveError'));
      else {
        setRaffleSkinId('');
        setRaffleDrawsAt('');
        setRaffleMax('');
        loadAll();
      }
    } catch {
      setError(t('adminShop.saveError'));
    } finally {
      setRaffleBusy(false);
    }
  };

  /**
   * Dra ett lotteri direkt eller avbryt det (med återbetalning).
   *
   * @param {object} raffle Lotteriet.
   * @param {string} action 'draw' eller 'cancel'.
   */
  const raffleAction = async (raffle, action) => {
    setRaffleBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/raffles/${raffle.id}`, {
        method: action === 'draw' ? 'POST' : 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) loadAll();
      else setError((await res.json()).error || t('adminShop.saveError'));
    } catch {
      setError(t('adminShop.saveError'));
    } finally {
      setRaffleBusy(false);
    }
  };

  /**
   * Avlista en vara (varan raderas aldrig — historiken behövs).
   *
   * @param {object} item Varan som avlistas.
   */
  const delist = async (item) => {
    try {
      const res = await fetch(`${API_URL}/api/shop/admin/items/${item.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) loadAll();
    } catch {
      setError(t('adminShop.saveError'));
    }
  };

  /**
   * Allokera poäng ur communitypotten till vald turnerings prispott.
   */
  const allocatePot = async () => {
    if (!potTournamentId || !potAmount) return;
    setPotBusy(true);
    setPotMsg('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/predictions/house-pot/allocate`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ tournament_id: potTournamentId, amount: Number(potAmount) }),
      });
      const body = await res.json();
      if (res.ok) {
        setPotMsg(t('adminShop.potAllocated', { amount: potAmount, total: body.pool_total }));
        setPotAmount('');
        loadAll();
      } else {
        setError(body.error || t('adminShop.saveError'));
      }
    } catch {
      setError(t('adminShop.saveError'));
    } finally {
      setPotBusy(false);
    }
  };

  if (loading) return <div className="admin-shop-loading">{t('adminShop.loading')}</div>;

  const activeItems = items.filter((i) => i.is_active);
  const inactiveItems = items.filter((i) => !i.is_active);

  return (
    <div className="admin-shop">
      <h2 className="admin-shop-title">🛒 {t('adminShop.title')}</h2>
      {error && <div className="admin-shop-error">{error}</div>}

      {housePot && (
        <div className="admin-shop-create admin-house-pot">
          <h3>🏦 {t('adminShop.potTitle')}</h3>
          <p className="admin-house-pot-info">
            {t('adminShop.potInfo', { balance: housePot.balance, pct: housePot.recycle_pct })}
          </p>
          <div className="admin-shop-create-row">
            <select
              className="admin-shop-select"
              value={potTournamentId}
              onChange={(e) => setPotTournamentId(e.target.value)}
            >
              <option value="">{t('adminShop.potPickTournament')}</option>
              {potTournaments.map((tn) => (
                <option key={tn.id} value={tn.id}>{tn.name}</option>
              ))}
            </select>
            <input
              className="admin-shop-select"
              type="number"
              min={1}
              max={housePot.balance}
              placeholder={t('adminShop.potAmountPlaceholder')}
              value={potAmount}
              onChange={(e) => setPotAmount(e.target.value)}
            />
            <button
              type="button"
              className="admin-shop-btn"
              disabled={potBusy || !potTournamentId || !potAmount || housePot.balance <= 0}
              onClick={allocatePot}
            >
              {potBusy ? '…' : t('adminShop.potAllocate')}
            </button>
          </div>
          {potMsg && <p className="admin-house-pot-msg">{potMsg}</p>}
        </div>
      )}

      <div className="admin-shop-create">
        <h3>{t('adminShop.listNew')}</h3>
        <div className="admin-shop-type-tabs">
          {['skin', 'custom', 'flair'].map((type) => (
            <button
              key={type}
              type="button"
              className={`admin-shop-type-tab ${newType === type ? 'active' : ''}`}
              onClick={() => setNewType(type)}
            >
              {t(`adminShop.type_${type}`)}
            </button>
          ))}
        </div>
        <div className="admin-shop-create-row">
          {newType === 'skin' ? (
            <select
              className="admin-shop-select"
              value={newSkinId}
              onChange={(e) => setNewSkinId(e.target.value)}
            >
              <option value="">{t('adminShop.pickSkin')}</option>
              {availableSkins.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.display_name}
                  {s.estimated_value_sek ? ` (≈${s.estimated_value_sek} SEK)` : ''}
                  {s.tradeable_at ? ` ⏳` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="admin-shop-select"
              type="text"
              placeholder={t('adminShop.titlePlaceholder')}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
          )}
          <input
            className="admin-shop-price-input"
            type="number"
            min="1"
            placeholder={t('adminShop.pricePlaceholder')}
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
          />
          <button
            type="button"
            className="admin-shop-add-btn"
            disabled={saving || !canCreate}
            onClick={createItem}
          >
            {saving ? t('adminShop.saving') : t('adminShop.addBtn')}
          </button>
        </div>
        {newType !== 'skin' && (
          <div className="admin-shop-create-row">
            <input
              className="admin-shop-select"
              type="text"
              placeholder={t('adminShop.descriptionPlaceholder')}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
            <input
              className="admin-shop-price-input"
              type="number"
              min="1"
              placeholder={t('adminShop.stockPlaceholder')}
              value={newStock}
              onChange={(e) => setNewStock(e.target.value)}
            />
          </div>
        )}
        {newType === 'flair' && (
          <div className="admin-shop-create-row">
            <select
              className="admin-shop-price-input admin-shop-flair-kind"
              value={newFlairKind}
              onChange={(e) => setNewFlairKind(e.target.value)}
            >
              <option value="title">{t('adminShop.flairTitle')}</option>
              <option value="badge">{t('adminShop.flairBadge')}</option>
              <option value="name_color">{t('adminShop.flairNameColor')}</option>
              <option value="emblem">{t('adminShop.flairEmblem')}</option>
            </select>
            {newFlairKind === 'emblem' ? (
              <select
                className="admin-shop-select"
                value={newFlairValue}
                onChange={(e) => setNewFlairValue(e.target.value)}
              >
                <option value="">{t('adminShop.pickEmblem')}</option>
                {availableEmblems.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.value}
                    {/* Redan listad märks ut men spärras inte — samma bild får
                        listas igen med annat pris eller lager. */}
                    {e.listed_as ? ` — ${t('adminShop.emblemListedAs', { title: e.listed_as })}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="admin-shop-select"
                type="text"
                placeholder={t(`adminShop.flairValuePlaceholder_${newFlairKind}`)}
                value={newFlairValue}
                onChange={(e) => setNewFlairValue(e.target.value)}
              />
            )}
            {newFlairKind === 'emblem' && newFlairValue && (
              <FlairEmblem emblem={newFlairValue} className="flair-emblem--preview" />
            )}
          </div>
        )}
        {newType === 'flair' && newFlairKind === 'emblem' && availableEmblems.length === 0 && (
          <p className="admin-shop-hint">{t('adminShop.noEmblemsHint')}</p>
        )}
        {newType === 'skin' && availableSkins.length === 0 && (
          <p className="admin-shop-hint">{t('adminShop.noSkinsHint')}</p>
        )}
      </div>

      <div className="admin-shop-create">
        <h3>🛡️ {t('adminShop.emblemGenTitle')}</h3>
        <p className="admin-shop-hint">{t('adminShop.emblemGenHelp')}</p>
        <div className="admin-shop-create-row">
          <input
            className="admin-shop-price-input admin-shop-emblem-name"
            type="text"
            placeholder={t('adminShop.emblemNamePlaceholder')}
            value={emblemName}
            onChange={(e) => setEmblemName(e.target.value)}
          />
          <input
            className="admin-shop-select"
            type="text"
            placeholder={t('adminShop.emblemMotifPlaceholder')}
            value={emblemMotif}
            onChange={(e) => setEmblemMotif(e.target.value)}
          />
          <button
            type="button"
            className="admin-shop-add-btn"
            disabled={emblemBusy || !emblemName.trim() || !emblemMotif.trim()}
            onClick={generateEmblem}
          >
            {emblemBusy ? t('adminShop.emblemGenerating') : t('adminShop.emblemGenerate')}
          </button>
        </div>
        {emblemError && <p className="admin-shop-error">{emblemError}</p>}
        {emblemResult && (
          <div className="admin-shop-emblem-result">
            <FlairEmblem emblem={emblemResult.value} className="flair-emblem--preview" />
            <div>
              <strong>{emblemResult.value}</strong>
              {/* Läsbarhetsmått: varna om dragningen kom ut mörk (grovfilter). */}
              <span className="admin-shop-hint">
                {t('adminShop.emblemReadability', {
                  coverage: Math.round((emblemResult.coverage || 0) * 100),
                  contrast: emblemResult.contrast,
                })}
              </span>
              {/* 3,0:1 är läsbarhetsgolvet mot panelen — under det kom bilden
                  ut mörk och admin bör dra om. */}
              {emblemResult.contrast < 3.0 && (
                <span className="admin-shop-error">{t('adminShop.emblemReadabilityLow')}</span>
              )}
              <span className="admin-shop-hint">{t('adminShop.emblemGenDone')}</span>
            </div>
          </div>
        )}
      </div>

      <div className="admin-shop-create">
        <h3>🎟️ {t('adminShop.raffleTitle')}</h3>
        <div className="admin-shop-create-row">
          <select
            className="admin-shop-select"
            value={raffleSkinId}
            onChange={(e) => setRaffleSkinId(e.target.value)}
          >
            <option value="">{t('adminShop.pickSkin')}</option>
            {availableSkins.map((s) => (
              <option key={s.id} value={s.id}>
                {s.display_name}
                {s.estimated_value_sek ? ` (≈${s.estimated_value_sek} SEK)` : ''}
              </option>
            ))}
          </select>
          <input
            className="admin-shop-price-input"
            type="number"
            min="1"
            placeholder={t('adminShop.rafflePricePlaceholder')}
            value={rafflePrice}
            onChange={(e) => setRafflePrice(e.target.value)}
          />
        </div>
        <div className="admin-shop-create-row">
          <input
            className="admin-shop-select"
            type="datetime-local"
            value={raffleDrawsAt}
            onChange={(e) => setRaffleDrawsAt(e.target.value)}
          />
          <input
            className="admin-shop-price-input"
            type="number"
            min="1"
            placeholder={t('adminShop.raffleMaxPlaceholder')}
            value={raffleMax}
            onChange={(e) => setRaffleMax(e.target.value)}
          />
          <button
            type="button"
            className="admin-shop-add-btn"
            disabled={raffleBusy || !raffleSkinId || !rafflePrice || !raffleDrawsAt}
            onClick={createRaffle}
          >
            {raffleBusy ? t('adminShop.saving') : t('adminShop.raffleCreateBtn')}
          </button>
        </div>
        {raffles.length > 0 && raffles.map((raffle) => (
          <div key={raffle.id} className={`admin-shop-row ${raffle.status !== 'open' ? 'inactive' : ''}`}>
            <span className="admin-shop-row-name">
              {raffle.prize.display_name}
              <span className="admin-shop-row-type">{raffle.status}</span>
            </span>
            <span className="admin-shop-row-price">
              🪙 {raffle.ticket_price} × {raffle.total_tickets}
            </span>
            <span className="admin-shop-row-date">
              {new Date(raffle.draws_at).toLocaleString()}
            </span>
            {raffle.status === 'open' && (
              <>
                <button
                  type="button"
                  className="admin-shop-save-btn"
                  disabled={raffleBusy}
                  onClick={() => raffleAction(raffle, 'draw')}
                >
                  {t('adminShop.raffleDrawBtn')}
                </button>
                <button
                  type="button"
                  className="admin-shop-delist-btn"
                  disabled={raffleBusy}
                  onClick={() => raffleAction(raffle, 'cancel')}
                >
                  {t('adminShop.raffleCancelBtn')}
                </button>
              </>
            )}
            {raffle.status === 'drawn' && (
              <span className="admin-shop-row-status status-sent">
                🏆 {raffle.winner_name}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="admin-shop-section">
        <h3>{t('adminShop.activeItems')} ({activeItems.length})</h3>
        {activeItems.length === 0 ? (
          <p className="admin-shop-hint">{t('adminShop.noActiveItems')}</p>
        ) : (
          activeItems.map((item) => (
            <div key={item.id} className="admin-shop-row">
              <span className="admin-shop-row-name">
                {item.display_name}
                <span className="admin-shop-row-type">{t(`adminShop.type_${item.item_type}`)}</span>
                {item.stock !== null && item.item_type !== 'skin' && (
                  <span className="admin-shop-row-type">×{item.stock}</span>
                )}
              </span>
              <input
                className="admin-shop-price-input"
                type="number"
                min="1"
                value={editPrices[item.id] ?? item.price_points}
                onChange={(e) => setEditPrices((prev) => ({ ...prev, [item.id]: e.target.value }))}
              />
              {editPrices[item.id] !== undefined
                && Number(editPrices[item.id]) !== item.price_points && (
                <button type="button" className="admin-shop-save-btn" onClick={() => savePrice(item)}>
                  {t('adminShop.saveBtn')}
                </button>
              )}
              <label className="admin-shop-tradeable" title={t('adminShop.tradeableHint')}>
                <input
                  type="checkbox"
                  checked={!!item.marketplace_tradeable}
                  onChange={() => toggleTradeable(item)}
                />
                {t('adminShop.tradeableLabel')}
              </label>
              <button type="button" className="admin-shop-delist-btn" onClick={() => delist(item)}>
                {t('adminShop.delistBtn')}
              </button>
            </div>
          ))
        )}
      </div>

      {purchases.length > 0 && (
        <div className="admin-shop-section">
          <h3>{t('adminShop.purchases')} ({purchases.length})</h3>
          {purchases.map((p) => (
            <div key={p.id} className="admin-shop-row purchase">
              <span className="admin-shop-row-name">
                {p.player_name} — {p.display_name}
              </span>
              <span className="admin-shop-row-price">🪙 {p.price_paid}</span>
              <span className={`admin-shop-row-status status-${p.trade_status}`}>
                {p.trade_status}
              </span>
              {p.item_type === 'custom' && p.trade_status === 'pending' && (
                <button
                  type="button"
                  className="admin-shop-save-btn"
                  disabled={deliveringId === p.id}
                  onClick={() => deliver(p)}
                >
                  {deliveringId === p.id ? t('adminShop.saving') : t('adminShop.deliverBtn')}
                </button>
              )}
              <span className="admin-shop-row-date">
                {new Date(p.created_at).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {inactiveItems.length > 0 && (
        <div className="admin-shop-section">
          <h3>{t('adminShop.inactiveItems')} ({inactiveItems.length})</h3>
          {inactiveItems.map((item) => (
            <div key={item.id} className="admin-shop-row inactive">
              <span className="admin-shop-row-name">
                {item.display_name}
                <span className="admin-shop-row-type">{t(`adminShop.type_${item.item_type}`)}</span>
              </span>
              <span className="admin-shop-row-price">🪙 {item.price_points}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
